/**
 * Shared sprite placement math for world-engine + OfficePreview.
 *
 * Convention:
 * - Footprint is the furniture's grid rect (gx, gy, w, d).
 * - Sprite foot sits at the **iso center** of that footprint:
 *     center = iso(gx + w/2, gy + d/2)
 * - Image is drawn bottom-centered on that point:
 *     x = center.x - width/2 + anchor.ax
 *     y = center.y - height + anchor.ay
 * - Art should be bottom-aligned in the PNG (content near the bottom edge).
 * - anchor.ax / anchor.ay are fine-tune nudges only (default 0).
 *
 * Outdoor buildings are special:
 * - Collision footprint is a shallow solid shell (see BUILDING_SHELL_SIZE).
 * - Draw uses natural pixel scale (see buildingDrawScale), NOT footprint-fill,
 *   so facades stay proportional next to trees/avatars (no stretch).
 */

import type { SpriteVariant } from "@/lib/office/sprites/types";

export type SpriteDrawRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Compute drawImage destination for a furniture sprite.
 * @param centerX iso X of footprint center
 * @param centerY iso Y of footprint center
 * @param variant sprite pixel size + optional anchor nudge
 * @param scale 1 in world engine; previewTw/64 in OfficePreview
 */
export function spriteDrawRect(
  centerX: number,
  centerY: number,
  variant: Pick<SpriteVariant, "width" | "height" | "anchor">,
  scale = 1,
): SpriteDrawRect {
  const width = variant.width * scale;
  const height = variant.height * scale;
  const ax = (variant.anchor?.ax ?? 0) * scale;
  const ay = (variant.anchor?.ay ?? 0) * scale;
  return {
    x: centerX - width / 2 + ax,
    y: centerY - height + ay,
    width,
    height,
  };
}

/**
 * Scale a multi-tile outdoor structure sprite so its width fills the iso footprint.
 * `tileW` is the engine tile width in px (64 in world-engine).
 * `fill` leaves a small margin so art doesn't clip neighboring tiles.
 *
 * Prefer for trucks/flowerbeds — NOT outdoor buildings (use buildingDrawScale).
 */
export function spriteDrawRectForFootprint(
  centerX: number,
  centerY: number,
  variant: Pick<SpriteVariant, "width" | "height" | "anchor">,
  footprintW: number,
  footprintD: number,
  tileW: number,
  fill = 0.92,
): SpriteDrawRect {
  const fw = footprintW > 0 ? footprintW : 1;
  const fd = footprintD > 0 ? footprintD : 1;
  const tw = tileW > 0 ? tileW : 64;
  const isoW = ((fw + fd) * tw) / 2;
  const assetW = variant.width > 0 ? variant.width : 1;
  const scale = Math.max(0.01, (isoW * fill) / assetW);
  return spriteDrawRect(centerX, centerY, variant, scale);
}

/**
 * Inclusive solid shell (tiles). Door is the mid tile on the south face of this
 * rect (force-walked); every other shell tile is blocked.
 *
 * Shallow depth (3): solid enough you cannot walk through; short enough the
 * plaza approach stays open. Width tuned to village-ref streetscape.
 */
export const BUILDING_SHELL_SIZE: Record<string, { w: number; d: number }> = {
  cafe: { w: 6, d: 3 },
  people_marketing: { w: 5, d: 3 },
  reception: { w: 5, d: 3 },
  delivery: { w: 6, d: 3 },
  post_delivery: { w: 5, d: 3 },
  customer_success: { w: 6, d: 3 },
  nymbl_hq: { w: 8, d: 5 },
};

export const BUILDING_SHELL_SIZE_DEFAULT = { w: 5, d: 3 } as const;

export function buildingShellSize(style?: string): { w: number; d: number } {
  if (style && BUILDING_SHELL_SIZE[style]) {
    return BUILDING_SHELL_SIZE[style];
  }
  return { w: BUILDING_SHELL_SIZE_DEFAULT.w, d: BUILDING_SHELL_SIZE_DEFAULT.d };
}

/**
 * Natural draw scale vs catalog PNG pixels.
 * Trees are 64×72 @ scale 1. Village-ref buildings ~3–3.5× tree height (~220–250px)
 * while keeping asset aspect (never footprint-stretch). Delivery asset is wide —
 * keep its scale lower so it doesn't crush neighbors.
 */
export const BUILDING_DRAW_SCALE: Record<string, number> = {
  cafe: 1.28,
  people_marketing: 1.32,
  reception: 1.05,
  delivery: 1.18,
  post_delivery: 1.28,
  customer_success: 1.28,
  // Procedural pixel-art HQ (376×376 native @ 2×) — flagship presence on the plaza.
  nymbl_hq: 1.2,
};

export const BUILDING_DRAW_SCALE_DEFAULT = 1.25;

export function buildingDrawScale(style?: string): number {
  if (style && BUILDING_DRAW_SCALE[style] !== undefined) {
    return BUILDING_DRAW_SCALE[style];
  }
  return BUILDING_DRAW_SCALE_DEFAULT;
}

/** @deprecated Prefer buildingShellSize */
export const BUILDING_VISUAL_FOOTPRINT = BUILDING_SHELL_SIZE;
export const BUILDING_VISUAL_FOOTPRINT_DEFAULT = BUILDING_SHELL_SIZE_DEFAULT;
export function buildingVisualFootprint(style?: string): { w: number; d: number } {
  return buildingShellSize(style);
}

/** Grid center of a furniture footprint (fractional grid coords). */
export function footprintCenter(gx: number, gy: number, w = 1, d = 1): { cx: number; cy: number } {
  const fw = w > 0 ? w : 1;
  const fd = d > 0 ? d : 1;
  return { cx: gx + fw / 2, cy: gy + fd / 2 };
}
